import React from 'react'

import MuiDialog from '@mui/material/Dialog'
import { DialogProps as MuiDialogProps } from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import type { SxProps } from '@mui/material/styles'

import { DialogFooter, DialogHeader } from '@mxenabled/mxui'
import { useScreenSize } from '../../hooks'

export type CopyObject = {
  close_aria?: string
  title: string
}

export interface DialogProps extends Omit<MuiDialogProps, 'open'> {
  className?: string
  copy: CopyObject
  disablePrimaryButton?: boolean
  isOpen: boolean
  onClose: (event?: unknown, reason?: string) => void
  onPrimaryAction?: () => void
  onSecondaryAction?: () => void
  primaryColor?: 'primary' | 'inherit' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  primaryText?: string
  secondaryColor?: 'primary' | 'inherit' | 'secondary' | 'success' | 'error' | 'info' | 'warning'
  secondaryText?: string
}

const Dialog: React.FC<React.PropsWithChildren<DialogProps>> = ({
  className,
  copy,
  disablePrimaryButton = false,
  isOpen = false,
  onClose,
  onPrimaryAction,
  primaryColor = 'primary',
  primaryText = 'Confirm',
  onSecondaryAction,
  secondaryColor = 'primary',
  secondaryText = 'Cancel',
  children,
  ...rest
}) => {
  // Extract sx from rest to merge with Dialog's own styles instead of overwriting
  const { sx: restSx, ...restProps } = rest
  const title = copy?.title ?? ''
  const closeAria = copy?.close_aria ?? 'Close'

  // Label the dialog *by* its visible heading (WAI-ARIA APG) instead of a duplicate
  // aria-label, so screen readers announce the title once rather than name + heading.
  const titleId = React.useId()

  const { isSmallMobile } = useScreenSize()

  const handlePrimaryAction = () => {
    onPrimaryAction?.()
    onClose()
  }

  const handleSecondaryAction = () => {
    if (onSecondaryAction) {
      onSecondaryAction()
    }
    onClose()
  }

  return (
    <div className={`mx-cmn-dialog ${className}`}>
      <MuiDialog
        aria-labelledby={titleId}
        maxWidth="xs"
        onClose={onClose}
        open={isOpen}
        sx={
          [
            {
              '& .MuiDialog-paperScrollPaper': { backgroundColor: 'background.default' },
              ...(isSmallMobile && { '& .MuiDialog-paper': { minWidth: '100%' } }),
              height: '100%',
            },
            ...(restSx ? [restSx] : []),
          ] as SxProps
        }
        {...restProps}
      >
        <DialogHeader
          ariaHidden={true}
          copy={{ title, close_aria: closeAria }}
          id={titleId}
          onClose={onClose}
          truncate={true}
        />

        <DialogContent sx={{ lineHeight: 1.5 }}>{children}</DialogContent>

        <DialogFooter
          isPrimaryDisabled={disablePrimaryButton}
          onPrimaryAction={handlePrimaryAction}
          onSecondaryAction={handleSecondaryAction}
          primaryColor={primaryColor}
          primaryText={primaryText}
          secondaryColor={secondaryColor}
          secondaryText={secondaryText}
        />
      </MuiDialog>
    </div>
  )
}

export default Dialog
